import { isUndefined } from 'lodash-es';
import { useIntl } from 'react-intl';

import { TrendProps } from '../../core/components/single-value-renderer/components/trend/types/trend-props.js';
import { SingleValueRenderer } from '../../core/components/single-value-renderer/SingleValueRenderer.js';
import type { SparklineDisplayProps } from '../../core/components/sparkline-display/types/sparkline-display-props.js';
import { SingleValueGridMessages } from '../messages.js';
import type {
  SingleValueGridAccessorsConfig,
  SingleValueGridConfigWithPropsFromGrid,
} from '../types/single-value-grid.js';
import { buildConfig } from '../utils/build-config.js';
import { buildPropsFromGrid } from '../utils/build-props-from-grid.js';
import { getConfigFromAccessors } from '../utils/get-config-from-accessors.js';
import { getDataFromAccessors } from '../utils/get-data-from-accessors.js';

export const SingleValueDisplay = ({
  record,
  propsConfig,
  accessors,
}: {
  record: string | number | Record<string, unknown>;
  propsConfig: SingleValueGridConfigWithPropsFromGrid;
  accessors: SingleValueGridAccessorsConfig;
}) => {
  const intl = useIntl();
  const gridIntents = propsConfig.intents.grid;
  const singleIntents = propsConfig.intents.single;
  const propsFromGrid = buildPropsFromGrid(
    propsConfig.toolbar,
    singleIntents,
    gridIntents,
    intl.formatMessage(SingleValueGridMessages.gridIntentsLabel),
  );

  if (typeof record === 'string' || typeof record === 'number') {
    // in case it is a simple array of strings or numbers, it will just use the props configuration or the defaults
    const config = buildConfig(propsConfig);

    return (
      <SingleValueRenderer
        data={record}
        color={config.color}
        formatter={config.value?.formatter}
        unit={config.value?.unit}
        colorRules={config.colorRules}
        alignment={config.alignment}
        trendProps={config.trend}
        applyThresholdBackground={config.applyThresholdBackground}
        propsFromGrid={propsFromGrid({ value: record })}
      />
    );
  }

  const { label, prefixIcon, value, trendData, sparkline } =
    getDataFromAccessors(record, accessors);

  // in case it is an array of records, it will override the config with the accessors configuration
  const accessorsConfig = getConfigFromAccessors(record, accessors);

  const config = buildConfig(propsConfig, accessorsConfig);

  let sparklineProps: SparklineDisplayProps | undefined = undefined;

  if (!isUndefined(sparkline?.data)) {
    sparklineProps = {
      ...config.sparkline,
      data: sparkline.data,
      xAxis: sparkline.xAxis,
      yAxis: sparkline.yAxis,
    };
  }

  let trendProps: TrendProps | undefined = undefined;

  if (!isUndefined(config.trend) || !isUndefined(trendData)) {
    trendProps = {
      ...config.trend,
      value: trendData?.value,
      label: trendData?.label,
      direction: trendData?.direction,
    };
  }

  return (
    <SingleValueRenderer
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      data={value!}
      dataItem={record}
      label={label}
      prefixIcon={prefixIcon}
      color={config.color}
      formatter={config.value?.formatter}
      unit={config.value?.unit}
      colorRules={config.colorRules}
      alignment={config.alignment}
      sparklineProps={sparklineProps}
      trendProps={trendProps}
      applyThresholdBackground={config.applyThresholdBackground}
      propsFromGrid={propsFromGrid(record)}
    />
  );
};
